// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Event,
  EventEmitter,
  Element
} from "@stencil/core";

import { MercuryBundles } from "@genexus/mercury";

const CSS_BUNDLES: MercuryBundles = ["resets/box-sizing", "components/icon"];

@Component({
  tag: "gx-ide-list-selector-item",
  styleUrl: "list-selector-item.scss",
  shadow: false
})
export class GxIdeListSelectorItem {
  // 1.OWN PROPERTIES | WATCH'S //

  private parentIsListSelector: boolean;
  private parentList: HTMLGxIdeListSelectorElement;
  private checked: boolean;
  private name: string;
  private type: string;
  private hasUniversalSlot = false;
  private index: string = undefined;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeListSelectorItemElement;

  // 3.STATE() VARIABLES //

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * The item id
   */
  @Prop({ reflect: true }) readonly itemId!: string;

  /**
   * The item value, that acts as a label as well
   */
  @Prop() readonly itemValue!: string;

  /**
   * This will set the input as checked
   */
  @Prop({ mutable: true }) itemChecked = false;

  /**
   * It allows to select the item by clicking on it. It will emit the item-id
   */
  @Prop({ mutable: true, reflect: true }) readonly: boolean;

  /**
   * The presence of this property will force the item text to not-wrap, and have ellipsis
   */
  @Prop({ mutable: true, reflect: true }) ellipsis: boolean;

  /**
   * The gemini icon name
   */
  @Prop() readonly icon: string;

  /**
   * Disables the item border
   */
  @Prop({ mutable: true, reflect: true }) noBorder = false;

  /**
   * Displays the universal slot at the end, instead of at the benign
   */
  @Prop({ reflect: true }) readonly universalSlotEnd = false;

  // 5.EVENTS (EMIT) //

  componentWillLoad() {
    this.evaluateParentIsListSelector();
    this.evaluateType();
    this.evaluateSelectable();
    this.evaluateEllipsis();
    this.evaluateChecked();
    this.evaluateBorder();
    this.evaluateUniversalSlot();
    this.assignName();
    this.evaluateOrderedList();
  }

  /**
   * Emits the item data
   */
  @Event() itemSelectionChanged: EventEmitter<ItemData>;

  // 6.COMPONENT LIFECYCLE METHODS //

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  /**
   * Checks if this item parentElement is a gx-ide-list-selector, and assigns a reference
   */
  private evaluateParentIsListSelector = () => {
    const parentList = this.el.parentElement as HTMLGxIdeListSelectorElement;
    this.parentIsListSelector = parentList?.nodeName === "GX-IDE-LIST-SELECTOR";
    this.parentIsListSelector && (this.parentList = parentList);
  };

  /**
   * Determines the type of item, based on the parent list-selector 'type' property value
   */
  private evaluateType = () => {
    const parentList: HTMLGxIdeListSelectorElement = this.el
      .parentElement as HTMLGxIdeListSelectorElement;
    this.type = parentList.type === "single-selection" ? "radio" : "checkbox";
  };

  /**
   * Evaluates if is selectable, considering the parent list-selector 'selectable' property value, only if it is undefined on this item
   */
  private evaluateSelectable = () => {
    if (this.readonly === undefined && this.parentIsListSelector) {
      this.readonly = this.parentList.readonly;
    }
  };

  /**
   * Evaluates if text should truncate with ellipsis, considering the parent list-selector 'ellipsis' property value, only if it is undefined on this item
   */
  private evaluateEllipsis = () => {
    if (this.ellipsis === undefined && this.parentIsListSelector) {
      this.ellipsis = this.parentList.ellipsis;
    }
  };

  /**
   * Evaluates if this item should be initially checked, only if the parent list-selector listValue equals this item value.
   */
  private evaluateChecked = () => {
    if (
      this.checked === undefined &&
      this.parentIsListSelector &&
      this.parentList.value !== undefined &&
      this.parentList.value === this.itemValue
    ) {
      this.checked = true;
    }
  };

  private evaluateBorder = () => {
    if (this.parentIsListSelector) {
      this.noBorder = this.parentList.noItemsBorder;
    }
  };

  private evaluateUniversalSlot = () => {
    this.hasUniversalSlot = !!this.el.querySelector("[slot=universal]");
  };

  /**
   * Gets the listName required value, that is needed for the list selection to properly work
   */
  private assignName = () => {
    this.parentIsListSelector &&
      this.parentList.listName &&
      (this.name = this.parentList.listName);
  };

  private evaluateOrderedList = () => {
    if (this.parentIsListSelector && this.parentList.ordered) {
      let index: number | string =
        Array.from(this.parentList.children).indexOf(this.el) + 1;
      if (index < 10) {
        // This will make descriptions to be nicely aligned, between one digit, and two digit indexes items.
        index = `0${index}`;
      } else {
        index = `${index}`;
      }
      this.index = index;
    }
  };

  private onChangeHandler = (e: Event) => {
    this.itemChecked = (e.target as HTMLInputElement).checked;
    this.itemSelectionChanged.emit({
      itemId: this.itemId,
      itemValue: this.itemValue,
      itemIcon: this.icon,
      itemChecked: this.itemChecked
    });
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host role="listitem">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <input
          type={this.type}
          id={this.itemId}
          name={this.name}
          value={this.itemValue}
          checked={this.itemChecked}
          onChange={this.onChangeHandler}
          disabled={this.readonly}
          part="input"
        />
        <label class="label" htmlFor={this.itemId} part="label">
          <div class="label__wrapper">
            {this.index ? (
              <span class="label__index">{this.index}. </span>
            ) : null}
            {this.hasUniversalSlot ? (
              <span class="label__universal-slot" part="universal-slot">
                <slot name="universal"></slot>
              </span>
            ) : null}
            {this.icon ? (
              <ch-image class="icon-s" src={this.icon} part="icon"></ch-image>
            ) : null}
            <span class="label__description" part="description">
              {this.itemValue ? this.itemValue : <slot></slot>}
            </span>
          </div>
        </label>
      </Host>
    );
  }
}

export type ItemData = {
  itemId: string;
  itemValue: string;
  itemIcon?: string;
  itemChecked: boolean;
};
